import React, { useState } from 'react';
import { DataGrid, GridActionsCellItem, GridRowId } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GridItem from 'components/Grid/GridItem';
import Button from 'components/CustomButtons/Button.js';
import { t } from 'i18next';
import { Discord } from './StaffDiscordContext';
import toast from 'react-hot-toast';
import { useTheme } from '@mui/material/styles';

interface StaffDiscordsTableProps {
  discords: Discord[];
  onEdit: (discord: Discord) => void;
  onDelete: (discord: Discord) => void;
  onDeleteSelected: (ids: string[]) => void;
}

const StaffDiscordsTable: React.FC<StaffDiscordsTableProps> = ({
  discords,
  onEdit,
  onDelete,
  onDeleteSelected,
}) => {
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleEdit = (row: Discord) => {
    onEdit(row);
  };

  const handleDelete = async (row: Discord) => {
    try {
      await onDelete(row);
      toast.success(t('discords.form.success.deleted'));
    } catch (error) {
      toast.error(t('discords.form.error.delete'));
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await onDeleteSelected(selectedRows.map((id) => id.toString()));
      toast.success(t('discords.form.success.deleted'));
    } catch (error) {
      toast.error(t('discords.form.error.delete'));
    }
  };

  return (
    <GridItem xs={12} sm={12} md={12}>
      <DataGrid
        rows={discords}
        columns={[
          {
            field: 'slug',
            headerName: t('discords.slug'),
            flex: 1,
          },
          {
            field: 'name',
            headerName: t('discords.name'),
            flex: 1,
          },
          {
            field: 'description',
            headerName: t('discords.description'),
            flex: 2,
          },
          {
            field: 'invite_link',
            headerName: t('discords.inviteLink'),
            flex: 1,
          },
          {
            field: 'rank',
            headerName: t('discords.rank'),
            flex: 1,
          },
          {
            field: 'logo_url',
            headerName: t('discords.logo'),
            flex: 1,
            renderCell: (params) =>
              params.value ? (
                <img
                  src={
                    typeof params.value === 'string'
                      ? params.value
                      : URL.createObjectURL(params.value)
                  }
                  alt="logo"
                  style={{ width: 50, height: 50, objectFit: 'contain' }}
                />
              ) : (
                <span>{t('discords.noLogo')}</span>
              ),
          },
          {
            field: 'published',
            headerName: t('discords.published'),
            flex: 1,
            type: 'boolean',
          },
          {
            field: 'actions',
            type: 'actions',
            headerName: t('common.dataGrid.rowActions'),
            flex: 1,
            minWidth: 90,
            getActions: (params) => [
              <GridActionsCellItem
                key="edit"
                icon={<EditIcon />}
                label={t('common.dataGrid.edit')}
                onClick={() => handleEdit(params.row)}
                color={isDarkMode ? 'inherit' : 'primary'}
              />,
              <GridActionsCellItem
                key="delete"
                icon={<DeleteIcon />}
                label={t('common.dataGrid.delete')}
                onClick={() => handleDelete(params.row)}
                color="error"
              />,
            ],
          },
        ]}
        checkboxSelection
        onRowSelectionModelChange={(newSelection) => {
          setSelectedRows(newSelection);
        }}
        disableRowSelectionOnClick
        autoHeight
        localeText={{
          noRowsLabel: t('common.dataGrid.noRows'),
          noResultsOverlayLabel: t('common.dataGrid.noResults'),
          toolbarColumns: t('common.dataGrid.toolbar.columns'),
          toolbarExport: t('common.dataGrid.toolbar.export'),
          toolbarFilters: t('common.dataGrid.toolbar.search'),
          columnMenuLabel: t('common.dataGrid.columnMenu.columns'),
          columnMenuShowColumns: t('common.dataGrid.columnMenu.columns'),
          columnMenuFilter: t('common.dataGrid.columnMenu.filter'),
          columnMenuHideColumn: t('common.dataGrid.columnMenu.hideColumn'),
          columnMenuSortAsc: `${t('common.dataGrid.columnMenu.sort')} (A-Z)`,
          columnMenuSortDesc: `${t('common.dataGrid.columnMenu.sort')} (Z-A)`,
          footerRowSelected: (count) =>
            count !== 1
              ? `${count} ${t('discords.deleteSelected')}`
              : `${count} ${t('discords.deleteSelected')}`,
          MuiTablePagination: {
            labelRowsPerPage: t('common.dataGrid.pagination.rowsPerPage'),
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} ${t('common.dataGrid.pagination.of')} ${count}`,
          },
        }}
      />
      <Button
        color="danger"
        onClick={handleDeleteSelected}
        disabled={selectedRows.length === 0}
        fullWidth
        style={{ marginTop: '20px' }}
      >
        {t('discords.deleteSelected')}
      </Button>
    </GridItem>
  );
};

export default StaffDiscordsTable;
